import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { LegalDoc } from '@/constants/legal'

import Link from 'next/link'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitText } from '@/components/generals/split-text'
import { Reveal } from '@/components/generals/reveal'
import { Rule } from '@/components/generals/rule'
import { legalDocs, getLegalDoc } from '@/constants/legal'
import { studioMeta } from '@/constants/site'

interface LegalPageProps {
    doc: LegalDoc
}

const LegalPage: FC<LegalPageProps> = ({ doc }): ReactNode => {
    return (
        <Layout theme='light'>
            <Seo title={doc.title} description={doc.intro} />

            <section
                id='legal-hero'
                className='flex flex-col gap-10 px-5 pb-12 pt-[calc(var(--nav-h)+52px)] lg:px-[33px] lg:pb-16 lg:pt-[calc(var(--nav-h)+92px)]'
            >
                <div className='mx-auto grid w-full max-w-[1920px] gap-8 lg:grid-cols-4 lg:gap-5'>
                    <span className='t-mono h-fit text-brand'>
                        Legal
                    </span>

                    <div className='flex flex-col gap-6 lg:col-span-3'>
                        <SplitText
                            as='h1'
                            text={doc.title}
                            trigger='mount'
                            stagger={0.016}
                            className='t-display text-[clamp(38px,7vw,110px)] leading-[0.88] text-ink'
                        />

                        <p className='t-cond max-w-[58ch] text-[clamp(17px,1.6vw,23px)] leading-[1.15] text-ash'>
                            {doc.intro}
                        </p>

                        <span className='t-mono text-ash'>
                            {doc.updated}
                        </span>
                    </div>
                </div>
            </section>

            <section
                id='legal-body'
                className='px-5 sec-b lg:px-[33px]'
            >
                <div className='mx-auto grid w-full max-w-[1920px] gap-8 lg:grid-cols-4 lg:gap-5'>
                    <nav className='h-fit lg:sticky lg:top-[calc(var(--nav-h)+24px)]' aria-label='Legal documents'>
                        <ul className='flex flex-col gap-2'>
                            {legalDocs.map((item) => (
                                <li key={item.slug}>
                                    <Link
                                        href={`/legal/${item.slug}`}
                                        data-cursor='link'
                                        aria-current={item.slug === doc.slug ? 'page' : undefined}
                                        className={item.slug === doc.slug
                                            ? 't-mono text-brand'
                                            : 't-mono text-ash transition-colors duration-300 hover:text-ink'}
                                    >
                                        {item.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex flex-col gap-12 lg:col-span-3'>
                        {doc.sections.map((section) => (
                            <Reveal
                                key={section.heading}
                                from='up'
                                distance={30}
                                className='flex flex-col gap-4'
                            >
                                <Rule className='text-ink' />

                                <h2 className='t-display text-[clamp(21px,2.4vw,30px)] leading-[1.1] text-ink'>
                                    {section.heading}
                                </h2>

                                {section.body.map((paragraph) => (
                                    <p
                                        key={paragraph}
                                        className='t-cond-soft max-w-[72ch] text-[15px] leading-[1.6] text-ash'
                                    >
                                        {paragraph}
                                    </p>
                                ))}
                            </Reveal>
                        ))}

                        <Reveal from='up' distance={24} className='flex flex-col gap-2 pt-4'>
                            <span className='t-mono text-ash'>
                                Questions about this page
                            </span>

                            <a
                                href={`mailto:${studioMeta.email}`}
                                data-cursor='link'
                                className='t-cond w-fit text-[clamp(17px,2vw,23px)] text-ink transition-colors duration-300 hover:text-brand'
                            >
                                {studioMeta.email}
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: legalDocs.map((doc) => ({ params: { slug: doc.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<LegalPageProps> = async ({ params }) => {
    const doc = getLegalDoc(String(params?.slug ?? ''))

    if (!doc) {
        return { notFound: true }
    }

    return { props: { doc } }
}

export default LegalPage
